/**
 * Mandar un análisis de Reactivación por WhatsApp oficial (Twilio): se crea el borrador de la campaña oficial
 * con los grupos del análisis como audiencia y se sigue en su asistente, en «Qué les decís». Lo usan el
 * asistente (`/activa/real`, «Por dónde y cuándo») y el detalle de una campaña por archivo.
 *
 * La oferta que llega al cliente sale del contexto guardado del análisis (`ofertaParaCliente` en el backend):
 * sin contexto saldría el % que propone el motor, que la marca no autorizó. Por eso un análisis sin contexto
 * (`analyzed`) no se manda desde acá: primero se elige la oferta en el asistente. Esto es para guiar: la
 * defensa es del backend, que no deja salir un mensaje con la «Oferta» sin ella (`ofertaElegida`,
 * OFERTA_SIN_ELEGIR), venga de acá o de la Audiencia del asistente de WhatsApp oficial.
 */

import { oficialApi } from "../../wa-oficial/api";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";
import type { CampanaResumen } from "../../wa-oficial/types";

export const MOTIVO_OFICIAL_EJEMPLO = "Son datos de ejemplo: no se mandan por WhatsApp oficial";
export const MOTIVO_OFICIAL_SIN_OFERTA = "Primero elegí la oferta en el asistente";

/** Por qué este análisis no se puede mandar por WhatsApp oficial desde acá, o null si se puede. */
export function motivoNoOficial(c: { estado?: string; esEjemplo?: boolean } | null | undefined): string | null {
  if (!c) return MOTIVO_OFICIAL_SIN_OFERTA;
  // el dataset de ejemplo trae teléfonos de personas ajenas a la marca: por el oficial saldrían de verdad
  if (c.esEjemplo) return MOTIVO_OFICIAL_EJEMPLO;
  if (!c.estado || c.estado === "draft" || c.estado === "analyzed") return MOTIVO_OFICIAL_SIN_OFERTA;
  return null;
}

/** Ruta del asistente de WhatsApp oficial para una campaña recién creada desde un análisis: ya tiene a quién, sigue en el mensaje. */
export const rutaOficialDesdeAnalisis = (idOficial: string) => `/activa/oficial/campanas/${encodeURIComponent(idOficial)}/editar?paso=mensaje`;

/** Ruta del detalle de una campaña de WhatsApp oficial que ya se confirmó. */
export const rutaDetalleOficial = (idOficial: string) => `/activa/oficial/campanas/${encodeURIComponent(idOficial)}`;

/**
 * La campaña de WhatsApp oficial que ya salió de este análisis (su audiencia es este análisis), o null. Se sigue ésa
 * en vez de crear otra: cada toque de «Mandar por WhatsApp oficial» (o «Seguir en WhatsApp oficial» del asistente)
 * creaba un borrador nuevo con la misma audiencia, y la lista de campañas oficiales se llenaba de copias (QA del
 * 08/10). Primero un borrador (se sigue armando, en el paso donde quedó); si no hay, la última que no se canceló (su
 * detalle: ya está confirmada, mandar otra con la misma gente sería repetirle el mensaje). Una cancelada no cuenta.
 * La lista llega de la más nueva a la más vieja (`listar` del backend).
 */
export function campanaOficialDelAnalisis(campanas: CampanaResumen[], analisisId: string): { id: string; ruta: string } | null {
  const delAnalisis = (campanas || []).filter((x) => x?.audiencia && x.audiencia.tipo !== "base" && x.audiencia.tipo !== "publico" && x.audiencia.campanaActivaId === analisisId);
  const borrador = delAnalisis.find((x) => x.estado === "borrador");
  // sin `?paso=`: el asistente abre el último paso alcanzado, no la plantilla de nuevo
  if (borrador) return { id: borrador.id, ruta: `/activa/oficial/campanas/${encodeURIComponent(borrador.id)}/editar` };
  const otra = delAnalisis.find((x) => x.estado !== "cancelada");
  return otra ? { id: otra.id, ruta: rutaDetalleOficial(otra.id) } : null;
}

export const NO_SE_PUDO_REVISAR_OFICIAL = "No pudimos revisar si ya hay una campaña de WhatsApp oficial con este análisis. Probá de nuevo en un rato.";

/**
 * A dónde ir para mandar este análisis por WhatsApp oficial: la campaña que ya salió de él (`campanaOficialDelAnalisis`)
 * o, si no hay ninguna, una nueva (`crearCampanaOficialDesde`). Si no se puede saber si ya hay una, no se crea otra a
 * ciegas: vuelve el error. Nunca lanza.
 */
export async function seguirPorOficial(c: { id: string; nombre?: string }): Promise<{ ok: true; ruta: string } | { ok: false; error: string }> {
  try {
    const l = leerRespuesta<CampanaResumen[]>(await oficialApi.campanas());
    if (!l.ok) return { ok: false, error: textoError(l) };
    const ya = campanaOficialDelAnalisis(Array.isArray(l.data) ? l.data : [], c.id);
    if (ya) return { ok: true, ruta: ya.ruta };
  } catch {
    return { ok: false, error: NO_SE_PUDO_REVISAR_OFICIAL };
  }
  const nueva = await crearCampanaOficialDesde(c);
  return nueva.ok ? { ok: true, ruta: rutaOficialDesdeAnalisis(nueva.id) } : nueva;
}

/** Crea el borrador de la campaña de WhatsApp oficial con todos los grupos del análisis. Nunca lanza. */
export async function crearCampanaOficialDesde(c: { id: string; nombre?: string }): Promise<{ ok: true; id: string } | { ok: false; error: string }> {
  const nombre = `${String(c.nombre || "Reactivación").slice(0, 90)} · WhatsApp oficial`;
  try {
    const l = leerRespuesta<{ id: string }>(await oficialApi.crearCampana({ nombre, audiencia: { tipo: "activa", campanaActivaId: c.id, grupos: [] } }));
    if (!l.ok) return { ok: false, error: textoError(l) };
    if (!l.data?.id) return { ok: false, error: "WhatsApp oficial no devolvió la campaña creada. Fijate en WhatsApp → Campañas." };
    return { ok: true, id: l.data.id };
  } catch (e) {
    return { ok: false, error: textoError(leerError(e)) };
  }
}
